import { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import {
    baseUrl,
    extractDefaultFilterValues,
    HtmlReactParser,
    removeEmptyDefaultFilterValues,
    translate,
    useMediaQuery,
} from "@kaltura/mediaspace-shared-utils";
import { Truncate } from "@kaltura/mediaspace-shared-ui";
import { FilterProps, FilterValues, SupportedKmsTypeFilter } from "@kaltura/mediaspace-shared-types";
import { buildQuerystring } from "kaltura-typescript-client/adapters/utils";
import {
    StyledAllContentContainer,
    StyledContentFiltersContainer,
    StyledDescription,
    StyledFiltersContainer,
    StyledGlobalSearch,
    StyledResultsContainer,
    StyledSearchFormContainer,
    StyledTitle,
} from "./styled-components";
import {
    FilterBar,
    FilterBarOpenButton,
    FilterMenuProps,
    SearchBar,
    SearchResultsContainer,
} from "@kaltura/mediaspace-shared-search";
import { SearchResultsData } from "./search-results/search-results-data/SearchResultsData";
import SearchResultsSkeleton from "./search-results/search-results-skeleton/SearchResultsSkeleton";
import { KalturaESearchEntryResult } from "kaltura-typescript-client/api/types/KalturaESearchEntryResult";

export interface GlobalSearchProps {
    title: string;
    description?: string;
    filters: FilterProps<SupportedKmsTypeFilter>[];
    filterMenuProps: FilterMenuProps;
    initialValues?: FilterValues;
    pageSize: number;
}

const baseClassName = "kms-ds-global-search";

/**
 * global search page
 */
export function GlobalSearch(props: GlobalSearchProps) {
    const { title, description, filters, filterMenuProps, initialValues = {}, pageSize } = props;

    // filter values as the user set them (+ defaults)
    const [filterValues, setFilterValues] = useState(() => {
        return { ...extractDefaultFilterValues(filters), ...initialValues };
    });

    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [selectedSearchResult, setSelectedSearchResult] = useState<KalturaESearchEntryResult>();

    const theme = useTheme();
    const mdScreen = useMediaQuery(theme.breakpoints.up("md"));
    const lgDownScreen = useMediaQuery(theme.breakpoints.down("lg"));

    const filtersButtonRef = useRef<HTMLButtonElement>(null);

    // reset filter modal state
    useEffect(() => {
        setIsFiltersModalOpen((isFiltersModalOpen) => isFiltersModalOpen && !mdScreen);
    }, [mdScreen]);

    // handle filter change
    const handleFilterChange = (values: FilterValues) => {
        setFilterValues(values);
        setSelectedSearchResult(undefined);
    };

    // handle submit search form
    const handleKeywordSearchSubmit = (value: string) => {
        setFilterValues((filterValues) => ({
            ...filterValues,
            keyword: value,
        }));
        setSelectedSearchResult(undefined);
    };

    // handle filter menu change
    const handleFilterMenuChange = (values: FilterValues) => {
        setFilterValues((filterValues) => ({ ...filterValues, ...values }));
        setSelectedSearchResult(undefined);
    };

    // minimal filter values - no empties, no defaults
    // will be recalculated on filters or filterValues change
    const minFilterValues = useMemo(
        () => removeEmptyDefaultFilterValues(filters, filterValues),
        [filters, filterValues]
    );

    // update history on filter change
    useEffect(() => {
        const updateHistory = (filterValues: FilterValues) => {
            // update query string
            const queryString = buildQuerystring(filterValues);
            window.history.pushState(null, "", `?${queryString}`);
        };

        updateHistory(minFilterValues);
    }, [filters, minFilterValues]);

    // hide filters (show in modal) in md size when result selected
    const modalFilters = selectedSearchResult && lgDownScreen;

    // on screens smaller than md, show button to open filters modal
    let searchBarButtons;
    if (!mdScreen || modalFilters) {
        searchBarButtons = [<FilterBarOpenButton ref={filtersButtonRef} onClick={() => setIsFiltersModalOpen(true)} />];
    }

    // the selected filter menu value
    const filterMenuValue = filterValues[filterMenuProps.param];
    // the selected filter menu label
    const filterMenuLabel =
        filterMenuValue === filterMenuProps.defaultValue
            ? undefined
            : filterMenuProps.items.find((item) => item.value === filterMenuValue)?.label;

    return (
        <StyledGlobalSearch className={baseClassName}>
            <StyledTitle variant="h2" component="h1">
                {title}
            </StyledTitle>

            {description && (
                <StyledDescription variant="body1" component="div">
                    <Truncate
                        lines={3}
                        showMoreTranslatedText={translate("Read more")}
                        showLessTranslatedText={translate("Read less")}
                    >
                        {HtmlReactParser(description)}
                    </Truncate>
                </StyledDescription>
            )}

            <StyledAllContentContainer
                className={`${baseClassName}-content-container`}
                baseClassName={baseClassName}
                onClose={() => setSelectedSearchResult(undefined)}
                searchResult={selectedSearchResult}
                filterValues={minFilterValues}
                mediaContext={"globalsearch"}
            >
                <StyledContentFiltersContainer inModal={modalFilters}>
                    <StyledFiltersContainer inModal={modalFilters} className={`${baseClassName}-filters-container`}>
                        <StyledSearchFormContainer
                            inModal={modalFilters}
                            className={`${baseClassName}-search-form-container`}
                        >
                            <SearchBar
                                buttons={searchBarButtons}
                                keywordSearchProps={{
                                    onSubmit: handleKeywordSearchSubmit,
                                    value: filterValues["keyword"] as string,
                                    placeholder: translate("Search"),
                                    ariaLabel: translate("Search for site content"),
                                }}
                                filterMenuProps={{
                                    ...filterMenuProps,
                                    values: filterValues,
                                    onChange: handleFilterMenuChange,
                                }}
                            />
                        </StyledSearchFormContainer>

                        <FilterBar
                            filters={filters}
                            values={filterValues}
                            onChange={handleFilterChange}
                            isModalOpen={isFiltersModalOpen}
                            inModal={modalFilters}
                            onModalClose={() => {
                                setIsFiltersModalOpen(false);
                                // when closing filters modal, focus the button that opened it:
                                filtersButtonRef.current?.focus();
                            }}
                        />
                    </StyledFiltersContainer>

                    <StyledResultsContainer className={`${baseClassName}-results-container`}>
                        <SearchResultsContainer
                            searchUrl={`${baseUrl}/default/global-search/search-results`}
                            isWideLayout={!selectedSearchResult}
                            filterValues={minFilterValues}
                            pageSize={pageSize}
                            onEntryShowMore={(item) => setSelectedSearchResult(item)}
                            searchInLabel={filterMenuLabel}
                            searchResultsDisplayComponent={SearchResultsData}
                            searchResultsSkeletonComponent={SearchResultsSkeleton}
                        />
                    </StyledResultsContainer>
                </StyledContentFiltersContainer>
            </StyledAllContentContainer>
        </StyledGlobalSearch>
    );
}

export default GlobalSearch;
